"use client";

// Chapitre « Progresser » : le tableau de bord, calqué sur le vrai (barre
// latérale repliée, accueil, formation à reprendre, puis quêtes du jour et
// ligue dans la colonne de droite). L'anneau de l'objectif du jour en a été
// retiré le 8 octobre 2026, en même temps que du vrai tableau de bord.
//
// Chaque changement a sa cause, dans l'ordre où l'œil les suit : le temps
// passé remplit la quête « Apprends pendant 15 minutes » ; la quête devient
// à récupérer, le curseur clique
// sur le coffre ; les XP gagnés font passer Léa devant Karim, dans la zone
// de promotion de sa ligue ; enfin « Reprendre » ramène au premier
// chapitre et referme la boucle.
//
// Les rangs de la ligue bougent par translation calculée (rang × hauteur
// de ligne) et non par animation de mise en page : la scène est mise à
// l'échelle par une transformation CSS, que les mesures de mise en page
// ne compensent pas.

import { AnimatePresence, motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { useAt, useCount } from "../demoClock";
import { useTarget } from "../DemoCursor";
import Icon from "../icons";
import { BrandIcon, CourseIcon, StreakCapsule } from "../sceneParts";

const T = {
    fillStart: 600,
    fillEnd: 1700,
    claim: 2700,
    xpStart: 2900,
    xpEnd: 3500,
    resume: 5300,
};

export const script = {
    duration: 6100,
    still: 3900,
    cursor: [
        // Le curseur va d'abord à la quête qui se remplit, pour y mener
        // l'œil, puis clique sur le coffre quand il apparaît.
        { at: 1500, target: "dashboard.quest", ax: 0.62, ay: 0.3, move: 900 },
        { at: T.claim, target: "dashboard.claim", click: true, move: 500 },
        { at: T.resume, target: "dashboard.resume", click: true, move: 1000 },
    ],
};

const GOAL = 15;
const REWARD = 25;

// Minutes du jour : elles remplissent la quête de temps.
function useMinutes() {
    return useCount(T.fillStart, T.fillEnd, 11, GOAL);
}

function Today() {
    const t = useTranslations();
    const locale = useLocale();
    const date = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }).format(new Date());

    return (
        <p className="mb-0 mt-1 text-[13.5px]">
            <span suppressHydrationWarning className="inline-block font-medium text-gray-800 first-letter:uppercase dark:text-grayDark-100">
                {date}
            </span>{" "}
            <span className="text-gray-500 dark:text-grayDark-400">- {t("showcase_dashboard_praise")}</span>
        </p>
    );
}

function ResumeCard({ compact }) {
    const t = useTranslations();
    const resumeRef = useTarget("dashboard.resume");
    const pressed = useAt(T.resume);

    if (compact) {
        return (
            <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 dark:border-grayDark-700 dark:bg-bgDark-700">
                <CourseIcon className="h-9 w-9 rounded-xl" />
                <div className="min-w-0 flex-1">
                    <p className="mb-0 text-[13px] font-bold text-gray-900 dark:text-white">Claude Code</p>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-grayDark-800">
                        <div className="h-full w-[64%] rounded-full bg-primary" />
                    </div>
                </div>
                <span
                    ref={resumeRef}
                    className={`flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12px] font-semibold text-white transition-colors duration-150 ${
                        pressed ? "bg-[#c9441a]" : "bg-primary"
                    }`}
                >
                    <Icon name="play" className="h-3 w-3" />
                    {t("showcase_dashboard_resume")}
                </span>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-grayDark-700 dark:bg-bgDark-700">
            <p className="mb-0 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-grayDark-500">
                {t("showcase_dashboard_courses")}
            </p>
            <div className="mt-4 flex items-center gap-3">
                <CourseIcon className="h-12 w-12 rounded-xl" />
                <div className="min-w-0 flex-1">
                    <p className="mb-0 text-[17px] font-bold text-gray-900 dark:text-white">Claude Code</p>
                    <div className="mt-1.5 flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-grayDark-800">
                            <div className="h-full w-[64%] rounded-full bg-primary" />
                        </div>
                        <span className="text-[11px] font-semibold tabular-nums text-gray-500 dark:text-grayDark-400">64 %</span>
                    </div>
                </div>
            </div>
            <div className="mt-4 rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                <p className="mb-0 text-[11px] text-gray-400 dark:text-grayDark-500">{t("showcase_dashboard_next")}</p>
                <p className="mb-0 mt-0.5 text-[14px] font-semibold text-gray-800 dark:text-grayDark-100">
                    {t("showcase_course_s3")}
                </p>
            </div>
            <span
                ref={resumeRef}
                className={`mt-4 flex h-11 items-center justify-center gap-2 rounded-xl text-[14px] font-semibold text-white transition-colors duration-150 ${
                    pressed ? "bg-[#c9441a]" : "bg-primary"
                }`}
            >
                <Icon name="play" className="h-3.5 w-3.5" />
                {t("showcase_dashboard_resume")}
            </span>
        </div>
    );
}

// Une quête, avec les codes du vrai composant : jaune tant qu'elle avance
// ou attend d'être récupérée, vert une fois récompensée.
function Quest({ icon, label, current, target, reward, state, claimRef, questRef, compact }) {
    const done = current >= target;
    const tone =
        state === "rewarded"
            ? "border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 dark:border-green-700/50 dark:from-green-900/20 dark:to-emerald-900/20"
            : state === "claimable"
              ? "border-yellow-200 bg-gradient-to-r from-yellow-50 to-amber-50 dark:border-yellow-700/50 dark:from-yellow-900/20 dark:to-amber-900/20"
              : "border-gray-100 bg-white dark:border-white/10 dark:bg-white/5";

    return (
        <div ref={questRef} className={`relative rounded-lg border transition-colors duration-300 ${tone} ${compact ? "p-2" : "p-2.5"}`}>
            <div className="flex items-center gap-2.5">
                <Image src={icon} alt="" width={18} height={18} className="h-[18px] w-[18px] shrink-0" />
                <span
                    className={`min-w-0 flex-1 truncate font-medium ${compact ? "text-[11.5px]" : "text-[12.5px]"} ${
                        done ? "text-green-800 dark:text-green-200" : "text-gray-900 dark:text-white"
                    }`}
                >
                    {label}
                </span>
                <span className="relative flex h-6 min-w-6 shrink-0 items-center justify-center">
                    <AnimatePresence mode="wait" initial={false}>
                        {state === "rewarded" ? (
                            <motion.span
                                key="rewarded"
                                initial={{ opacity: 0, scale: 0.6 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                                className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-white"
                            >
                                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5">
                                    <path
                                        fill="currentColor"
                                        d="m9.55 15.15l8.475-8.475q.3-.3.7-.3t.7.3t.3.713t-.3.712l-9.175 9.2q-.3.3-.7.3t-.7-.3L4.55 13q-.3-.3-.288-.712t.313-.713t.713-.3t.712.3z"
                                    />
                                </svg>
                            </motion.span>
                        ) : state === "claimable" ? (
                            <motion.span
                                key="chest"
                                ref={claimRef}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                className="flex h-6 w-6 items-center justify-center rounded-md bg-white/70 ring-1 ring-yellow-300 dark:bg-white/10"
                            >
                                <Image src="/static/icons/chests/common.svg" alt="" width={18} height={18} className="h-[18px] w-[18px]" />
                            </motion.span>
                        ) : (
                            <motion.span key="reward" className="text-[11px] font-medium text-gray-400 dark:text-grayDark-400">
                                +{reward} XP
                            </motion.span>
                        )}
                    </AnimatePresence>
                </span>
            </div>
            <div className={`relative mt-2 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10 ${compact ? "h-3" : "h-3.5"}`}>
                <div
                    className={`h-full rounded-full transition-[width] duration-300 ease-out ${done ? "bg-emerald-400" : "bg-yellow-400"}`}
                    style={{ width: `${Math.min(100, (current / target) * 100)}%` }}
                />
                <span
                    className={`absolute inset-0 flex items-center justify-center font-medium tabular-nums ${compact ? "text-[9px]" : "text-[10px]"} ${
                        done ? "text-emerald-900" : "text-gray-600 dark:text-white"
                    }`}
                >
                    {current} / {target}
                </span>
            </div>

            {/* Les XP de la récompense s'élèvent du coffre et s'effacent. */}
            <AnimatePresence>
                {state === "rewarded" && reward && (
                    <motion.span
                        key="gain"
                        initial={{ opacity: 1, y: 0 }}
                        animate={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.9, ease: "easeOut" }}
                        className="pointer-events-none absolute -top-1 right-2 text-[12px] font-bold text-amber-600 dark:text-amber-400"
                    >
                        +{reward} XP
                    </motion.span>
                )}
            </AnimatePresence>
        </div>
    );
}

function Quests({ compact }) {
    const t = useTranslations();
    const questRef = useTarget("dashboard.quest");
    const claimRef = useTarget("dashboard.claim");
    const minutes = useMinutes();
    const claimed = useAt(T.claim);
    const timeState = claimed ? "rewarded" : minutes >= GOAL ? "claimable" : "progress";

    return (
        <div className={`rounded-2xl border border-gray-200/60 bg-white shadow-sm dark:border-borderDark dark:bg-bgDark-840 ${compact ? "p-3" : "p-4"}`}>
            <div className={`flex items-center gap-2.5 ${compact ? "mb-2.5" : "mb-3"}`}>
                <span className={`flex items-center justify-center rounded-xl bg-purple-50 dark:bg-purple-500/10 ${compact ? "h-7 w-7" : "h-8 w-8"}`}>
                    <svg className="h-4 w-4 text-purple-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                        <path d="M3 8.71c0-1.474 0-2.21.393-2.64a1.5 1.5 0 01.497-.36c.532-.236 1.231-.003 2.629.463c1.067.356 1.6.534 2.14.515a3 3 0 00.588-.078c.525-.125.993-.437 1.929-1.06l1.382-.922c1.2-.8 1.799-1.2 2.487-1.291c.688-.093 1.372.135 2.739.591l1.165.388c.99.33 1.485.495 1.768.888S21 6.12 21 7.162v8.129c0 1.473 0 2.21-.393 2.64a1.5 1.5 0 01-.497.358c-.532.237-1.231.004-2.629-.462c-1.067-.356-1.6-.534-2.14-.515a3 3 0 00-.588.078c-.525.125-.993.437-1.929 1.06l-1.382.922c-1.2.8-1.799 1.2-2.487 1.291c-.688.093-1.372-.135-2.739-.591l-1.165-.388c-.99-.33-1.485-.495-1.768-.888S3 17.88 3 16.838zm6-2.071V20.5M15 3v14" />
                    </svg>
                </span>
                <span className={`font-bold text-gray-900 dark:text-white ${compact ? "text-[13px]" : "text-[14px]"}`}>
                    {t("showcase_dashboard_quests")}
                </span>
            </div>
            <div className="space-y-2">
                <Quest
                    icon="/static/icons/booknote.svg"
                    label={t("showcase_dashboard_quest_session")}
                    current={1}
                    target={1}
                    state="rewarded"
                    compact={compact}
                />
                <Quest
                    icon="/static/icons/clock.svg"
                    label={t("showcase_dashboard_quest_time", { minutes: GOAL })}
                    current={minutes}
                    target={GOAL}
                    reward={REWARD}
                    state={timeState}
                    questRef={questRef}
                    claimRef={claimRef}
                    compact={compact}
                />
                {!compact && (
                    <Quest
                        icon="/static/icons/xp.svg"
                        label={t("showcase_dashboard_quest_xp", { xp: 50 })}
                        current={20}
                        target={50}
                        reward={30}
                        state="progress"
                    />
                )}
            </div>
        </div>
    );
}

const PLAYERS = [
    { key: "thomas", name: "Thomas R.", initial: "T", xp: 1320, color: "bg-sky-500" },
    { key: "ines", name: "Inès B.", initial: "I", xp: 1260, color: "bg-rose-400" },
    { key: "karim", name: "Karim D.", initial: "K", xp: 1230, color: "bg-amber-500" },
];

function League({ compact }) {
    const t = useTranslations();
    const locale = useLocale();
    const myXp = useCount(T.xpStart, T.xpEnd, 1220, 1220 + REWARD, 5);
    const rowHeight = compact ? 28 : 34;
    const promotion = 3;
    const gap = 20;

    const you = { key: "you", name: t("leagues.you"), initial: "L", xp: myXp, color: "bg-[#1f4e4a]", you: true };
    const ranking = [...PLAYERS, you].sort((a, b) => b.xp - a.xp);
    const rankOf = (key) => ranking.findIndex((player) => player.key === key) + 1;
    // Sur mobile, le premier de la ligue s'efface : la place manque, et
    // l'histoire se joue entre la 2e et la 4e place.
    const rows = compact ? [PLAYERS[1], PLAYERS[2], you] : [...PLAYERS, you];
    const firstRank = compact ? 2 : 1;
    const top = (rank) => (rank - firstRank) * rowHeight + (rank > promotion ? gap : 0);

    return (
        <div className={`rounded-2xl border border-gray-200/60 bg-white shadow-sm dark:border-borderDark dark:bg-bgDark-840 ${compact ? "p-3" : "p-4"}`}>
            <div className="flex items-center gap-2.5">
                <Image src="/static/icons/leagues/3.svg" alt="" width={36} height={36} className={compact ? "h-7 w-7" : "h-8 w-8"} />
                <div className="min-w-0 flex-1">
                    <p className="mb-0 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-grayDark-500">
                        {t("leagues.current_league")}
                    </p>
                    <p className={`mb-0 font-bold text-gray-900 dark:text-white ${compact ? "text-[13px]" : "text-[14px]"}`}>
                        {t("leagues.3")}
                    </p>
                </div>
                <span className="text-[11px] text-gray-400 dark:text-grayDark-500">3 {t("leagues.days_left")}</span>
            </div>

            <div className={`relative ${compact ? "mt-2" : "mt-3"}`} style={{ height: rowHeight * rows.length + gap }}>
                {rows.map((player) => {
                    const rank = rankOf(player.key);
                    return (
                        <motion.div
                            key={player.key}
                            initial={false}
                            animate={{ y: top(rank) }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className={`absolute inset-x-0 top-0 flex items-center gap-2.5 rounded-lg px-2 ${
                                player.you ? "bg-primary/[0.07] dark:bg-primary/15" : ""
                            }`}
                            style={{ height: rowHeight - 4 }}
                        >
                            <span className="w-3.5 text-center text-[11px] font-bold tabular-nums text-gray-400 dark:text-grayDark-500">
                                {rank}
                            </span>
                            <span
                                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${player.color}`}
                            >
                                {player.initial}
                            </span>
                            <span
                                className={`min-w-0 flex-1 truncate text-[12px] ${
                                    player.you ? "font-semibold text-gray-900 dark:text-white" : "text-gray-600 dark:text-grayDark-300"
                                }`}
                            >
                                {player.name}
                            </span>
                            <span
                                className={`text-[11.5px] tabular-nums ${
                                    player.you ? "font-semibold text-gray-900 dark:text-white" : "text-gray-500 dark:text-grayDark-400"
                                }`}
                            >
                                {player.xp.toLocaleString(locale)} XP
                            </span>
                        </motion.div>
                    );
                })}
                <div
                    className="absolute inset-x-0 flex items-center gap-2 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400"
                    style={{ top: (promotion - firstRank + 1) * rowHeight - 2, height: gap }}
                >
                    <span className="h-px flex-1 bg-emerald-500/30" />
                    <Icon name="arrow-up" className="h-3 w-3" />
                    {t("leagues.promotion_zone", { number: promotion })}
                    <span className="h-px flex-1 bg-emerald-500/30" />
                </div>
            </div>
        </div>
    );
}

const NAV = [
    { key: "dashboard", icon: "dashboard", active: true },
    { key: "readingList", icon: "bookmark" },
    { key: "leagues", icon: "leagues" },
    { key: "courses", icon: "courses" },
    { key: "reps", icon: "reps" },
    { key: "articles", icon: "articles" },
    { key: "glossaries", icon: "glossary" },
    { key: "toolbox", icon: "tools" },
    { key: "accelerators", icon: "fire-line" },
];

// La barre latérale en mode replié, comme l'application la propose.
function Rail() {
    return (
        <aside className="flex w-[60px] shrink-0 flex-col items-center border-r border-gray-100 bg-white dark:border-grayDark-800 dark:bg-bgDark-800">
            <div className="flex h-14 items-center">
                <BrandIcon className="h-7 w-7" />
            </div>
            <nav className="mt-2 flex flex-col items-center gap-1">
                {NAV.map((item) => (
                    <span
                        key={item.key}
                        className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                            item.active
                                ? "bg-black/[0.08] text-[#05060F] dark:bg-white/[0.08] dark:text-white"
                                : "text-[#05060F]/55 dark:text-grayDark-500"
                        }`}
                    >
                        <Icon name={item.icon} className="h-5 w-5" />
                    </span>
                ))}
            </nav>
        </aside>
    );
}

function TopBar() {
    return (
        <div className="flex h-14 shrink-0 items-center justify-end gap-3 border-b border-gray-100 bg-white px-6 dark:border-grayDark-800 dark:bg-bgDark-800">
            <span className="inline-flex h-8 items-center gap-3 rounded-full border border-gray-200 bg-gray-50/80 px-3 text-[13px] font-bold tabular-nums text-gray-700 dark:border-grayDark-700 dark:bg-white/[0.04] dark:text-grayDark-200">
                <span className="flex items-center gap-1.5">
                    <Image src="/static/icons/streak.svg" alt="" width={16} height={16} className="h-4 w-4" />
                    12
                </span>
                <span className="flex items-center gap-1.5">
                    <Image src="/static/icons/fragment.svg" alt="" width={16} height={16} className="h-4 w-4" />
                    340
                </span>
                <span className="flex items-center gap-1.5">
                    <Image src="/static/icons/key.svg" alt="" width={16} height={16} className="h-4 w-4" />
                    2
                </span>
            </span>
            <Icon name="gift" className="h-5 w-5 text-gray-400 dark:text-grayDark-500" />
            <Icon name="bell" className="h-5 w-5 text-gray-400 dark:text-grayDark-500" />
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1f4e4a] text-[13px] font-semibold text-white">
                L
            </span>
        </div>
    );
}

// Formations bien réelles du catalogue, pour la rangée de recommandations.
const RECOMMENDED = [
    { name: "n8n", icon: "/images/site/courses/icon-n8n.png" },
    { name: "Claude Design", icon: "/images/site/courses/icon-claudedesign.png" },
];

export default function DashboardScene({ compact }) {
    const t = useTranslations();

    if (compact) {
        return (
            <div className="flex h-full flex-col bg-gray-50/50 dark:bg-bgDark-900/40">
                <header className="flex h-12 shrink-0 items-center gap-2 border-b border-gray-100 bg-white px-4 dark:border-grayDark-800 dark:bg-bgDark-800">
                    <BrandIcon className="h-6 w-6" />
                    <StreakCapsule days={12} small className="ml-auto" />
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#1f4e4a] text-[12px] font-semibold text-white">
                        L
                    </span>
                </header>
                <div className="space-y-2.5 px-4 pt-3">
                    <div className="flex items-center justify-between">
                        <p className="mb-0 text-[19px] font-bold text-gray-900 dark:text-white">
                            {t("showcase_dashboard_greeting", { name: "Léa" })}
                        </p>
                    </div>
                    <Quests compact />
                    <League compact />
                    <ResumeCard compact />
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full bg-gray-50/50 dark:bg-bgDark-900/40">
            <Rail />
            <div className="flex min-w-0 flex-1 flex-col">
                <TopBar />
                <div className="grid min-h-0 flex-1 grid-cols-[1fr_300px] gap-6 px-7 pt-6">
                    <div className="min-w-0">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="mb-0 text-[26px] font-bold leading-tight text-gray-900 dark:text-white">
                                    {t("showcase_dashboard_greeting", { name: "Léa" })}
                                </p>
                                <Today />
                            </div>
                        </div>
                        <div className="mt-6">
                            <ResumeCard />
                        </div>
                        <p className="mb-0 mt-6 text-[13px] font-semibold text-gray-900 dark:text-white">
                            {t("showcase_dashboard_recommended")}
                        </p>
                        <div className="mt-3 grid grid-cols-2 gap-4">
                            {RECOMMENDED.map((course) => (
                                <div
                                    key={course.name}
                                    className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3.5 dark:border-grayDark-700 dark:bg-bgDark-700"
                                >
                                    <Image src={course.icon} alt="" width={36} height={36} className="h-9 w-9 rounded-lg" />
                                    <span className="text-[13px] font-semibold text-gray-800 dark:text-grayDark-100">{course.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-4">
                        <Quests />
                        <League />
                    </div>
                </div>
            </div>
        </div>
    );
}
